<template>
  <section class="results-service">
    <div class="service-intro">
      <div><span class="eyebrow">JIANGK · EVENT</span><h3>{{ t(activeView === 'entries' ? 'entries' : activeView === 'claims' ? 'claims' : 'workspace') }}</h3><p>{{ t(activeView === 'entries' ? 'entryHelp' : activeView === 'claims' ? 'scope' : 'noRoster') }}</p></div>
      <a-button icon="reload" :loading="loading" @click="reload">{{ t('refresh') }}</a-button>
    </div>
    <participation-admin v-if="activeView === 'claims'" :event="event" :entries="entries" :categories="categories" />
    <template v-else>
      <a-alert v-if="!categories.length" type="info" show-icon :message="t('categoryFirst')" />
      <div class="draw-toolbar">
        <label>{{ t('category') }}<a-select v-model="categoryId" :disabled="busy" :placeholder="t('selectCategory')" @change="changeCategory">
          <a-select-option v-for="item in categories" :key="item.id" :value="item.id">{{ item.categoryName }}</a-select-option>
        </a-select></label>
        <label v-if="activeView === 'draw'" class="race-select">{{ t('race') }}<a-select v-model="roundId" :disabled="busy" :placeholder="t('selectRace')" @change="loadRows">
          <a-select-option v-for="item in categoryRounds" :key="item.id" :value="item.id">{{ raceLabel(item) }}</a-select-option>
        </a-select></label>
        <a-button v-if="activeView === 'draw'" icon="plus" :disabled="!categoryId" @click="openRace">{{ t('newRace') }}</a-button>
        <a-button v-if="activeView === 'draw'" :disabled="!roundId || busy" @click="editRace">{{ t('editRace') }}</a-button>
      </div>
      <div class="draw-actions">
        <a-button :disabled="!categoryId" @click="openEntry">{{ t('addEntry') }}</a-button>
        <a-button v-if="activeView === 'draw'" :disabled="!roundId" @click="openRow()">{{ t('addRow') }}</a-button>
        <a-button v-if="activeView === 'draw'" :disabled="!roundId" @click="openPaste">{{ t('paste') }}</a-button>
        <a-button v-if="activeView === 'draw'" type="primary" :disabled="!rows.length || busy || hasUnlinkedResults" @click="confirmPublish">{{ t('publish') }}</a-button>
      </div>
      <a-alert v-if="activeView === 'draw' && hasUnlinkedResults" type="warning" show-icon :message="t('unlinkedLegacy')" class="legacy-link-notice" />
      <a-table v-if="activeView === 'entries'" :data-source="categoryEntries" :columns="entryColumns" row-key="id" :loading="loading" :pagination="{ pageSize: 12, showSizeChanger: true }" :scroll="{ x: 620 }">
        <template slot="source" slot-scope="value, entry"><a-tag :color="entry.registrationId ? 'blue' : 'default'">{{ t(entry.registrationId ? 'sourceRegistration' : 'sourceDraw') }}</a-tag></template>
        <template slot="entryAction" slot-scope="value, entry"><a-tooltip :title="entry.registrationId ? t('registeredEntryHelp') : ''"><a-button type="link" :disabled="busy || !!entry.registrationId" @click="openEntry(entry)">{{ t('edit') }}</a-button></a-tooltip></template>
      </a-table>
      <a-table v-else :data-source="rows" :columns="columns" row-key="id" :loading="loading" :pagination="{ pageSize: 12, showSizeChanger: true }" :scroll="{ x: 900 }">
        <template slot="state" slot-scope="value, row">{{ t(resultState(row)) }}</template>
        <template slot="published" slot-scope="value"><a-tag :color="value === 1 ? 'green' : 'default'">{{ t(value === 1 ? 'published' : 'draft') }}</a-tag></template>
        <template slot="action" slot-scope="value, row">
          <template v-if="row.eventEntryId">
            <a-button type="link" :disabled="busy" @click="openRow(row)">{{ t('edit') }}</a-button>
            <a-button v-if="row.published !== 1" type="link" :disabled="busy" @click="removeDraft(row)">{{ t('removeDraft') }}</a-button>
          </template>
          <a-tag v-else>{{ t('unlinkedRecord') }}</a-tag>
        </template>
      </a-table>
      <p v-if="activeView === 'draw'" class="service-note">{{ t('publishHelp') }}</p>
    </template>

    <a-modal v-model="raceVisible" :title="t(raceForm.id ? 'editRace' : 'newRace')" :confirm-loading="busy" :mask-closable="false" :closable="!busy" :keyboard="!busy" :cancel-button-props="{ props: { disabled: busy } }" @ok="saveRace">
      <a-form-model layout="vertical">
        <single-source-content v-if="raceVisible" ref="raceContent" :value="raceForm" :fields="roundContentFields" kind="round" :event-id="event.id" />
        <a-form-item :label="t('raceTime')"><a-date-picker v-model="raceForm.roundTime" show-time value-format="YYYY-MM-DD HH:mm:ss" style="width:100%" /></a-form-item>
        <a-form-item :label="t('distance')"><a-input-number v-model="raceForm.distance" :min="1" :max="100000" /></a-form-item>
        <a-form-item :label="t('raceStatus')"><a-select v-model="raceForm.status"><a-select-option v-for="value in [0, 1, 2, 3]" :key="value" :value="value">{{ t('raceState' + value) }}</a-select-option></a-select></a-form-item>
      </a-form-model>
    </a-modal>
    <a-modal v-model="entryVisible" :title="t(entryForm.id ? 'edit' : 'addEntry')" :confirm-loading="busy" :mask-closable="false" @ok="saveEntry">
      <a-form layout="vertical">
        <a-form-item :label="t('entryCode')" required><a-input v-model="entryForm.entryCode" :max-length="40" /></a-form-item>
        <a-form-item :label="t('teamName')" required><a-input v-model="entryForm.teamName" :max-length="100" /></a-form-item>
      </a-form>
      <p class="service-note">{{ t('source') }}</p>
    </a-modal>
    <a-modal v-model="rowVisible" :title="t('workspace')" :confirm-loading="busy" :mask-closable="false" :ok-text="t('saveDraft')" @ok="saveRow">
      <a-form layout="vertical">
        <a-form-item :label="t('teamName')" required>
          <a-select v-model="rowForm.eventEntryId" :disabled="!!rowForm.id" show-search option-filter-prop="children">
            <a-select-option v-for="item in categoryEntries" :key="item.id" :value="item.id">{{ item.entryCode }} · {{ item.teamName }}</a-select-option>
          </a-select>
        </a-form-item>
        <div class="field-pair">
          <a-form-item :label="t('lane')"><a-input-number v-model="rowForm.laneNo" :min="1" :max="999" /></a-form-item>
          <a-form-item :label="t('bib')"><a-input v-model="rowForm.bibNo" :max-length="20" /></a-form-item>
        </div>
        <a-form-item :label="t('state')"><a-select v-model="rowForm.resultStatus" @change="changeResultStatus">
          <a-select-option v-for="state in resultStates" :key="state" :value="state">{{ t(state) }}</a-select-option>
        </a-select></a-form-item>
        <div v-if="rowForm.resultStatus === 'FINISHED'" class="field-pair">
          <a-form-item :label="t('rank')"><a-input-number v-model="rowForm.rankNo" :min="1" :max="99999" /></a-form-item>
          <a-form-item :label="t('time')"><a-input v-model="rowForm.resultTime" placeholder="02:15.320" :max-length="30" /></a-form-item>
        </div>
        <a-form-item :label="t('remark')"><a-textarea v-model="rowForm.remark" :max-length="255" :rows="2" /></a-form-item>
        <a-form-item v-if="rowForm.wasPublished" :label="t('correction')" required><a-textarea v-model="rowForm.correctionReason" :max-length="500" :rows="2" /><p class="service-note">{{ t('correctionHelp') }}</p></a-form-item>
      </a-form>
    </a-modal>
    <a-modal v-model="pasteVisible" :title="t('paste')" width="780px" :footer="null" :mask-closable="false" :closable="!busy" :keyboard="!busy">
      <p>{{ t('pasteHelp') }}</p>
      <a-textarea v-model="pasteText" :rows="6" :disabled="busy" placeholder="A01&#9;示例队伍&#9;1" @change="previewRows = []" />
      <div class="draw-actions"><a-button :disabled="busy" @click="previewPaste">{{ t('preview') }}</a-button><a-button type="primary" :loading="busy" :disabled="!previewRows.length" @click="importPaste">{{ t('importConfirm') }}</a-button></div>
      <a-alert v-if="importNote" type="info" :message="importNote" show-icon />
      <a-table v-if="previewRows.length" :data-source="previewRows" :columns="pasteColumns" row-key="entryCode" :pagination="{ pageSize: 8 }" />
    </a-modal>
  </section>
</template>

<script>
import request from '@/utils/request'
import { resultsText, parseDrawPaste } from '@/utils/results-service'
import ParticipationAdmin from './ParticipationAdmin.vue'
import SingleSourceContent from '@/components/SingleSourceContent.vue'
import { createContentDraft, contentPayload, ROUND_CONTENT_FIELDS } from '@/utils/single-source-content'

export default {
  components: { ParticipationAdmin, SingleSourceContent },
  props: { event: { type: Object, required: true }, categories: { type: Array, default: () => [] }, activeView: { type: String, default: 'draw' }, initialCategoryId: Number, initialRoundId: Number },
  data() {
    return {
      categoryId: undefined, roundId: undefined, entries: [], rounds: [], rows: [],
      loading: false, busy: false, requestSerial: 0, refreshSerial: 0,
      routeSelectionPending: true,
      disposed: false,
      raceVisible: false, raceForm: {}, roundContentFields: ROUND_CONTENT_FIELDS, entryVisible: false, entryForm: {}, rowVisible: false, rowForm: {},
      pasteVisible: false, pasteText: '', previewRows: [], importNote: '',
      resultStates: ['FINISHED', 'DNS', 'DNF', 'DSQ']
    }
  },
  computed: {
    hasUnlinkedResults() { return this.rows.some(row => !row.eventEntryId) },
    entryColumns() {
      return [
        { title: this.t('entryCode'), dataIndex: 'entryCode', width: 140 },
        { title: this.t('teamName'), dataIndex: 'teamName', width: 220 },
        { title: this.t('entrySource'), dataIndex: 'source', scopedSlots: { customRender: 'source' } },
        { title: this.t('action'), key: 'action', scopedSlots: { customRender: 'entryAction' } }
      ]
    },
    categoryEntries() { return this.entries.filter(item => item.categoryId === this.categoryId) },
    categoryRounds() { return this.rounds.filter(item => item.categoryId === this.categoryId) },
    columns() {
      return [
        { title: this.t('lane'), dataIndex: 'laneNo', width: 100 },
        { title: this.t('teamName'), dataIndex: 'teamName', width: 200 },
        { title: this.t('rank'), dataIndex: 'rankNo', customRender: value => value > 0 ? value : '—' },
        { title: this.t('time'), dataIndex: 'resultTime', customRender: value => value || '—' },
        { title: this.t('state'), dataIndex: 'resultStatus', scopedSlots: { customRender: 'state' } },
        { title: this.t('published'), dataIndex: 'published', scopedSlots: { customRender: 'published' } },
        { title: this.t('action'), key: 'action', scopedSlots: { customRender: 'action' } }
      ]
    },
    pasteColumns() { return ['entryCode', 'teamName', 'lane'].map(key => ({ title: this.t(key), dataIndex: key === 'lane' ? 'laneNo' : key })) }
  },
  watch: {
    categories: { immediate: true, handler(list) {
      if (!list.some(item => item.id === this.categoryId)) {
        this.categoryId = list.length ? list[0].id : undefined
        this.changeCategory()
      }
    } }
  },
  created() { this.reload() },
  beforeDestroy() { this.disposed = true; this.requestSerial++; this.refreshSerial++ },
  methods: {
    t(key) { return resultsText(this.$adminLocale, key) },
    api(url, params) { return this.disposed ? Promise.reject(new Error('Workspace closed')) : request({ url, params, silentError: true }) },
    post(url, data) { return this.disposed ? Promise.reject(new Error('Workspace closed')) : request({ url, method: 'post', data, silentError: true }) },
    report(error) { if (!this.disposed) this.$message.error(error.message || this.t('failed')) },
    raceLabel(row) { return `Race ${row.raceNo || '—'} · ${row.roundName} · ${(row.roundTime || '').replace('T', ' ') || this.t('pending')}` },
    async reload() {
      if (this.disposed) return false
      const refresh = ++this.refreshSerial
      this.loading = true
      try {
        const [entries, rounds] = await Promise.all([
          this.api('/dragonboat/event-entry/list', { eventId: this.event.id }),
          this.api('/dragonboat/round/options', { eventId: this.event.id })
        ])
        if (refresh !== this.refreshSerial) return false
        this.entries = entries.data.list || []
        this.rounds = rounds.data || []
        if (this.routeSelectionPending) {
          this.routeSelectionPending = false
          const selectedRace = this.rounds.find(item => item.id === this.initialRoundId && (!this.initialCategoryId || item.categoryId === this.initialCategoryId))
          const selectedCategory = selectedRace ? selectedRace.categoryId : this.initialCategoryId
          const categoryValid = this.categories.some(item => item.id === selectedCategory)
          if (categoryValid) this.categoryId = selectedCategory
          if (selectedRace && selectedRace.categoryId === this.categoryId) this.roundId = selectedRace.id
          this.$emit('route-resolved', { categoryId: categoryValid ? selectedCategory : undefined, roundId: selectedRace && categoryValid ? selectedRace.id : undefined })
        }
        if (!this.categoryRounds.some(item => item.id === this.roundId)) this.roundId = this.categoryRounds.length ? this.categoryRounds[0].id : undefined
        return await this.loadRows()
      } catch (error) { if (refresh === this.refreshSerial) this.report(error); return false } finally { if (refresh === this.refreshSerial) this.loading = false }
    },
    changeCategory() {
      this.roundId = this.categoryRounds.length ? this.categoryRounds[0].id : undefined
      this.loadRows()
    },
    async loadRows() {
      if (this.disposed) return false
      const serial = ++this.requestSerial
      this.rows = []
      if (!this.roundId) return true
      this.loading = true
      try {
        const query = { eventId: this.event.id, categoryId: this.categoryId, roundId: this.roundId, pageSize: 500 }
        const rows = []
        let page = 1, total = 0
        do {
          const response = await this.api('/dragonboat/result/list', { ...query, page })
          if (serial !== this.requestSerial) return false
          const list = response.data.list || []
          total = Number(response.data.total) || list.length
          if (!list.length && rows.length < total) throw new Error(this.t('failed'))
          rows.push(...list)
          page++
        } while (rows.length < total)
        this.rows = rows
        return true
      } catch (error) { if (serial === this.requestSerial) this.report(error); return false } finally { if (serial === this.requestSerial) this.loading = false }
    },
    openRace() { this.raceForm = createContentDraft({ roundName: '', description: '', roundTime: null, distance: null, stageCode: 'OTHER', heatNo: 1, status: 0 }, ROUND_CONTENT_FIELDS); this.raceVisible = true },
    editRace() {
      const round = this.categoryRounds.find(item => item.id === this.roundId)
      if (!round) return
      this.raceForm = createContentDraft(round, ROUND_CONTENT_FIELDS)
      this.raceVisible = true
    },
    async saveRace() {
      if (this.busy) return
      this.busy = true
      try {
        await this.$refs.raceContent.prepare()
        const response = await this.post('/dragonboat/round/save', { ...contentPayload(this.raceForm), eventId: this.event.id, categoryId: this.categoryId })
        this.roundId = response.data.id
        this.raceVisible = false
        await this.reload()
      } catch (error) { this.report(error) } finally { this.busy = false }
    },
    openEntry(entry) { this.entryForm = entry && entry.id ? { ...entry } : { entryCode: '', teamName: '' }; this.entryVisible = true },
    async saveEntry() {
      if (this.busy) return
      if (!this.entryForm.entryCode.trim() || !this.entryForm.teamName.trim()) return this.$message.warning(this.t('required'))
      this.busy = true
      try {
        await this.post('/dragonboat/event-entry/save', { ...this.entryForm, eventId: this.event.id, categoryId: this.categoryId })
        this.entryVisible = false
        await this.reload()
      } catch (error) { this.report(error) } finally { this.busy = false }
    },
    openRow(row) {
      if (row && row.id && !row.eventEntryId) return this.$message.warning(this.t('unlinkedLegacy'))
      const resultStatus = this.resultState(row)
      this.rowForm = row ? { ...row, rankNo: resultStatus === 'FINISHED' && row.rankNo > 0 ? row.rankNo : null, resultTime: resultStatus === 'FINISHED' ? row.resultTime : '', resultStatus, wasPublished: row.published === 1, correctionReason: '' } : { eventEntryId: undefined, laneNo: null, bibNo: '', rankNo: null, resultTime: '', resultStatus: 'FINISHED', remark: '' }
      this.rowVisible = true
    },
    resultState(row) {
      if (!row) return 'FINISHED'
      if (['DNS', 'DNF', 'DSQ'].includes(row.resultStatus)) return row.resultStatus
      const marker = String(row.resultTime || '').trim().toUpperCase()
      return ['DNS', 'DNF', 'DSQ'].includes(marker) ? marker : marker === '犯规' ? 'DSQ' : 'FINISHED'
    },
    changeResultStatus(value) { if (value !== 'FINISHED') { this.rowForm.rankNo = null; this.rowForm.resultTime = '' } },
    removeDraft(row) {
      if (this.busy || row.published === 1 || !row.eventEntryId) return
      this.$confirm({ title: `${this.t('removeDraft')} · ${row.teamName}`, content: this.t('removeHelp'), onOk: async () => {
        this.busy = true
        try {
          await this.post('/dragonboat/result/delete', { id: row.id, resultRevision: row.resultRevision })
          await this.loadRows()
        } catch (error) { this.report(error); throw error } finally { this.busy = false }
      } })
    },
    async saveRow() {
      if (this.busy) return
      if (!this.rowForm.eventEntryId || (this.rowForm.wasPublished && !this.rowForm.correctionReason.trim())) return this.$message.warning(this.t('required'))
      this.busy = true
      try {
        const payload = { ...this.rowForm, eventId: this.event.id, categoryId: this.categoryId, roundId: this.roundId, published: 0 }
        delete payload.wasPublished
        await this.post('/dragonboat/result/save', payload)
        this.rowVisible = false
        await this.loadRows()
      } catch (error) { this.report(error) } finally { this.busy = false }
    },
    confirmPublish() {
      if (this.busy || !this.roundId || !this.rows.length) return
      if (this.rows.some(row => !row.eventEntryId)) return this.$message.warning(this.t('unlinkedLegacy'))
      // Capture exactly what the operator reviewed, including the set of rows.
      const reviewed = {
        eventId: this.event.id, categoryId: this.categoryId, roundId: this.roundId, published: 1,
        expectedRevisions: Object.fromEntries(this.rows.map(row => [String(row.id), row.resultRevision]))
      }
      this.$confirm({ title: this.t('publish'), content: this.t('publishHelp'), okText: this.t('publish'), onOk: async () => {
        this.busy = true
        try {
          await this.post('/dragonboat/result/publish', reviewed)
          if (!this.disposed) this.$message.success(this.t('published'))
          await this.loadRows()
        } catch (error) { this.report(error); throw error } finally { this.busy = false }
      } })
    },
    openPaste() { this.pasteVisible = true; this.previewRows = []; this.importNote = '' },
    previewPaste() {
      try { this.previewRows = parseDrawPaste(this.pasteText, this.entries, this.categoryId); this.importNote = '' } catch (error) { this.previewRows = []; this.report(error) }
    },
    async importPaste() {
      if (this.busy || !this.previewRows.length) return
      this.busy = true
      let done = 0
      try {
        // Refresh identity mappings before applying the reviewed rows. Never merge by team name.
        if (!await this.reload()) throw new Error(this.t('failed'))
        if (!this.categoryId || !this.roundId) throw new Error(this.t('selectRace'))
        const preview = parseDrawPaste(this.pasteText, this.entries, this.categoryId)
        for (const row of preview) {
          const current = this.rows.find(item => item.eventEntryId === row.eventEntryId)
          if (current && (current.laneNo || null) !== row.laneNo) throw new Error(`${row.entryCode}: 已有对阵位不同，请单独核对 / Existing draw position differs`)
          const laneUsed = this.rows.find(item => row.laneNo && item.laneNo === row.laneNo && item.eventEntryId !== row.eventEntryId)
          if (laneUsed) throw new Error(`${row.entryCode}: 航道已占用 / Lane already assigned`)
        }
        for (const row of preview) {
          let entryId = row.eventEntryId
          if (!entryId) {
            const created = await this.post('/dragonboat/event-entry/save', { eventId: this.event.id, categoryId: this.categoryId, entryCode: row.entryCode, teamName: row.teamName })
            entryId = created.data.id
          }
          if (!this.rows.some(item => item.eventEntryId === entryId)) {
            await this.post('/dragonboat/result/save', { eventId: this.event.id, categoryId: this.categoryId, roundId: this.roundId, eventEntryId: entryId, laneNo: row.laneNo, rankNo: 0, resultTime: '', resultStatus: 'FINISHED', published: 0 })
          }
          done++
        }
        this.pasteVisible = false
        this.pasteText = ''
        this.previewRows = []
        if (!this.disposed) this.$message.success(this.t('saved'))
      } catch (error) {
        this.importNote = `${done}/${this.previewRows.length} 已处理 / processed. 已保存内容保留；重试不会重复建立相同编号。Saved entries are retained; retry uses the same codes.`
        this.report(error)
      } finally { this.busy = false; await this.reload() }
    }
  }
}
</script>

<style scoped>
.results-service { padding: 28px; color: #213547; }
.service-intro { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.eyebrow { color: #167f84; font-size: 11px; font-weight: 700; letter-spacing: .14em; }
h3 { font-size: 24px; margin: 5px 0 8px; }
.service-intro p, .service-note { color: #65798b; line-height: 1.7; font-size: 13px; }
.service-nav { margin: 12px 0 24px; }
.draw-toolbar { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; padding: 20px; margin: 0 0 16px; background: #f4f8fa; border: 1px solid #e3ecef; border-radius: 12px; }
.draw-toolbar label { display: flex; flex-direction: column; gap: 7px; min-width: 180px; font-size: 12px; font-weight: 600; }
.draw-toolbar .race-select { min-width: 290px; flex: 1; }
.draw-actions { display: flex; gap: 8px; flex-wrap: wrap; margin: 16px 0; }
.legacy-link-notice { margin-bottom: 16px; }
.field-pair { display: flex; gap: 20px; }
.field-pair > * { flex: 1; min-width: 0; }
@media(max-width: 640px) { .results-service { padding: 16px; } .draw-toolbar label, .draw-toolbar .race-select { min-width: 0; width: 100%; } .draw-toolbar .race-select { flex: 0 0 100%; } .field-pair { display: block; } }
</style>
